import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Mail, 
  MailOpen,
  ExternalLink,
  RefreshCw,
  Sparkles,
  Calendar,
  FileText,
  Shield,
  Server,
  Brain,
  Filter,
  TrendingUp,
  CheckCircle,
  XCircle
} from 'lucide-react';

const ClaraMailAI = () => {
  const [aiEmails, setAiEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [checkingOutlook, setCheckingOutlook] = useState(true);
  const [stats, setStats] = useState({
    invoices: 0,
    appointments: 0,
    contracts: 0,
    inquiries: 0,
    newsletters: 0,
    total: 0
  });

  // Check Outlook connection status
  const checkOutlookStatus = async () => {
    setCheckingOutlook(true);
    try {
      const response = await fetch('https://clara360.de/api/outlook/status');
      if (response.ok) {
        const data = await response.json();
        setOutlookConnected(data.authenticated || false);
      }
    } catch (err) {
      console.error('Error checking Outlook status:', err);
    } finally {
      setCheckingOutlook(false);
    }
  };

  // Connect to Outlook
  const connectOutlook = () => {
    window.location.href = 'https://clara360.de/api/auth/microsoft/login';
  };

  // Add appointment to Outlook Calendar
  const addToOutlookCalendar = async (email) => {
    if (!outlookConnected) {
      alert('Bitte verbinden Sie zuerst Ihr Outlook-Konto.');
      return;
    }

    if (!email.appointment) {
      alert('Keine Termininformationen gefunden.');
      return;
    }

    try {
      const response = await fetch('https://clara360.de/api/outlook/calendar/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email_id: email.id,
          subject: email.subject,
          sender: email.from,
          received_at: email.date,
          category: email.category,
          summary: email.summary,
          appointment_data: {
            title: email.subject,
            start_time: email.appointment.start_time,
            end_time: email.appointment.end_time,
            location: email.appointment.location,
            description: email.summary
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create calendar event');
      }

      const data = await response.json();
      alert(`✅ Termin erfolgreich zu Outlook Kalender hinzugefügt!\n\nTitel: ${data.event.subject}\nStart: ${new Date(data.event.start.dateTime).toLocaleString('de-DE')}`);
    } catch (err) {
      console.error('Error adding to Outlook:', err);
      alert('❌ Fehler beim Hinzufügen des Termins zu Outlook Kalender.');
    }
  };

  // Fetch AI-analyzed emails
  const fetchAIEmails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('https://clara360.de/api/clara-mail/audit-log?limit=20');
      if (!response.ok) {
        throw new Error('Failed to fetch AI data');
      }
      const data = await response.json();
      
      // Transform data
      const enriched = data.map(item => ({
        id: item.id,
        messageId: item.email_subject, // Use subject as ID for now
        subject: item.email_subject,
        from: item.email_from,
        to: item.email_to,
        date: new Date(item.timestamp).toLocaleString('de-DE'),
        category: item.result?.category || 'other',
        summary: item.result?.summary || null,
        appointment: item.result?.appointment || null,
        confidence: item.result?.confidence || 0,
        processingTime: item.processing_time_ms
      }));
      
      setAiEmails(enriched);
      
      // Calculate stats
      const newStats = {
        invoices: enriched.filter(e => e.category === 'invoice').length,
        appointments: enriched.filter(e => e.category === 'appointment').length,
        contracts: enriched.filter(e => e.category === 'contract').length,
        inquiries: enriched.filter(e => e.category === 'inquiry').length,
        newsletters: enriched.filter(e => e.category === 'newsletter').length,
        total: enriched.length
      };
      setStats(newStats);
      
    } catch (err) {
      console.error('Error fetching AI emails:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAIEmails();
    checkOutlookStatus();
  }, []);

  // Helper functions
  const getCategoryColor = (category) => {
    const colors = {
      invoice: 'bg-green-500',
      contract: 'bg-blue-500',
      appointment: 'bg-yellow-500',
      inquiry: 'bg-cyan-500',
      newsletter: 'bg-gray-500',
      other: 'bg-gray-300'
    };
    return colors[category] || colors.other;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      invoice: '💰',
      contract: '📄',
      appointment: '📅',
      inquiry: '❓',
      newsletter: '📰',
      other: '📧'
    };
    return icons[category] || icons.other;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      invoice: 'Rechnung',
      contract: 'Vertrag',
      appointment: 'Termin',
      inquiry: 'Anfrage',
      newsletter: 'Newsletter',
      other: 'Sonstiges'
    };
    return labels[category] || labels.other;
  };

  const filteredEmails = filter === 'all' 
    ? aiEmails 
    : aiEmails.filter(email => email.category === filter);

  const openInRoundcube = (subject) => {
    // Encode subject for URL
    const encoded = encodeURIComponent(subject);
    window.open(`https://mail.clara360.de/?_task=mail&_action=show&_search=${encoded}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <Card className="dark:bg-gray-800 dark:border-gray-700 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">Clara Mail Intelligence</h3>
              <p className="text-blue-100">
                KI-gestützte E-Mail-Analyse mit automatischer Kategorisierung und Zusammenfassungen
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => fetchAIEmails()}
                disabled={loading}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white border-white border-opacity-30"
                size="sm"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Aktualisieren
              </Button>
              <Button 
                onClick={() => window.open('https://mail.clara360.de/', '_blank')}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white border-white border-opacity-30"
                size="sm"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Roundcube
              </Button>
            </div>
          </div>
        </div>

        {/* Outlook Calendar Connection Status */}
        <div className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Outlook Kalender Sync
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {checkingOutlook ? (
                    'Status wird überprüft...'
                  ) : outlookConnected ? (
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <CheckCircle className="w-3 h-3" />
                      Verbunden - Termine werden automatisch synchronisiert
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                      <XCircle className="w-3 h-3" />
                      Nicht verbunden - Verbinden Sie Ihr Outlook-Konto
                    </span>
                  )}
                </p>
              </div>
            </div>
            {!checkingOutlook && !outlookConnected && (
              <Button 
                onClick={connectOutlook}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                <Calendar className="w-4 h-4 mr-1" />
                Mit Outlook verbinden
              </Button>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <Card className="dark:bg-gray-700">
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.invoices}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">💰 Rechnungen</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-gray-700">
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{stats.appointments}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">📅 Termine</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-gray-700">
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.contracts}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">📄 Verträge</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-gray-700">
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-cyan-600 dark:text-cyan-400">{stats.inquiries}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">❓ Anfragen</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-gray-700">
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-600 dark:text-gray-400">{stats.newsletters}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">📰 Newsletter</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-gray-700">
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{stats.total}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">📊 Gesamt</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            <Button 
              onClick={() => setFilter('all')}
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
            >
              <Filter className="w-3 h-3 mr-1" />
              Alle ({stats.total})
            </Button>
            <Button 
              onClick={() => setFilter('invoice')}
              variant={filter === 'invoice' ? 'default' : 'outline'}
              size="sm"
              className={filter === 'invoice' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              💰 Rechnungen ({stats.invoices})
            </Button>
            <Button 
              onClick={() => setFilter('appointment')}
              variant={filter === 'appointment' ? 'default' : 'outline'}
              size="sm"
              className={filter === 'appointment' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
            >
              📅 Termine ({stats.appointments})
            </Button>
            <Button 
              onClick={() => setFilter('contract')}
              variant={filter === 'contract' ? 'default' : 'outline'}
              size="sm"
              className={filter === 'contract' ? 'bg-blue-600 hover:bg-blue-700' : ''}
            >
              📄 Verträge ({stats.contracts})
            </Button>
            <Button 
              onClick={() => setFilter('inquiry')}
              variant={filter === 'inquiry' ? 'default' : 'outline'}
              size="sm"
              className={filter === 'inquiry' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
            >
              ❓ Anfragen ({stats.inquiries})
            </Button>
            <Button 
              onClick={() => setFilter('newsletter')}
              variant={filter === 'newsletter' ? 'default' : 'outline'}
              size="sm"
              className={filter === 'newsletter' ? 'bg-gray-600 hover:bg-gray-700' : ''}
            >
              📰 Newsletter ({stats.newsletters})
            </Button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-red-600 dark:text-red-400 text-sm">
                Fehler beim Laden der AI-Daten: {error}
              </p>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">AI-Analysen werden geladen...</span>
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="text-center py-12">
              <MailOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                {filter === 'all' ? 'Keine E-Mails analysiert' : `Keine ${getCategoryLabel(filter)} gefunden`}
              </p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[800px] overflow-y-auto">
              {filteredEmails.map((email, index) => (
                <Card key={email.id || index} className="dark:bg-gray-700 hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h5 className="font-semibold text-gray-900 dark:text-white text-lg truncate">
                            {email.subject}
                          </h5>
                          <Badge className={`${getCategoryColor(email.category)} text-white text-xs px-2 py-1`}>
                            {getCategoryIcon(email.category)} {getCategoryLabel(email.category)}
                          </Badge>
                          {email.confidence > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {Math.round(email.confidence * 100)}% Confidence
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Von: {email.from}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          {email.date}
                        </p>
                      </div>
                    </div>

                    {/* AI Summary */}
                    {email.summary && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 mb-3 rounded">
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                              AI-Zusammenfassung:
                            </p>
                            <p className="text-sm text-blue-800 dark:text-blue-200">
                              {email.summary}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Appointment */}
                    {email.appointment && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 mb-3 rounded">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-2 flex-1">
                            <Calendar className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mb-1">
                                Extrahierter Termin:
                              </p>
                              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                📅 {email.appointment.date} um {email.appointment.time} Uhr
                              </p>
                              {email.appointment.location && (
                                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                  📍 {email.appointment.location}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-2">
                            {outlookConnected && (
                              <Button 
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => addToOutlookCalendar(email)}
                              >
                                <Calendar className="w-3 h-3 mr-1" />
                                Zu Outlook
                              </Button>
                            )}
                            <Button 
                              size="sm"
                              className="bg-yellow-600 hover:bg-yellow-700 text-white"
                              onClick={() => window.open(`https://hub.clara360.de/apps/calendar/`, '_blank')}
                            >
                              <Calendar className="w-3 h-3 mr-1" />
                              Nextcloud
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <TrendingUp className="w-3 h-3" />
                        Verarbeitet in {email.processingTime}ms
                      </div>
                      <Button 
                        size="sm"
                        onClick={() => openInRoundcube(email.subject)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        E-Mail öffnen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardContent className="pt-6">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Sicher & Privat</h4>
            <p className="text-gray-600 dark:text-gray-300">
              Ihre E-Mails werden verschlüsselt übertragen und gespeichert.
            </p>
          </CardContent>
        </Card>
        
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardContent className="pt-6">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mb-4">
              <Server className="w-6 h-6 text-green-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Eigene Infrastruktur</h4>
            <p className="text-gray-600 dark:text-gray-300">
              Volle Kontrolle über Ihre Daten mit selbst-gehosteter Lösung.
            </p>
          </CardContent>
        </Card>
        
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardContent className="pt-6">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mb-4">
              <Brain className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">KI-Integration</h4>
            <p className="text-gray-600 dark:text-gray-300">
              Intelligente E-Mail-Analyse und automatische Kategorisierung.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClaraMailAI;

